// Pure, framework-free distance math + copy -- no dependency pulled in
// for what's a dozen lines of trigonometry (see this task's own brief).
// This is a from-scratch TypeScript implementation of the same
// haversine formula `backend/internal/domain/geo.go`'s DistanceMeters
// uses (not a port -- kept independent on purpose, same as this app's
// other client-side pure-logic files, e.g. lib/avatar-identity.ts).
//
// Why this exists at all rather than reusing the backend's own
// BucketDistance: Thread.location is deliberately public/exact (unlike
// the jittered Profile.location the app shows for *other* users), so
// there's no privacy reason to bucket a thread's distance -- see
// design.md's Geo-privacy section and BucketDistance's own doc comment,
// which is specifically about another user's profile, not a thread.

export interface Coordinates {
  lat: number;
  lng: number;
}

// Mean Earth radius in meters -- same constant geo.go's DistanceMeters
// uses, so the two implementations agree on the same real-world
// distances even though neither imports the other.
const EARTH_RADIUS_METERS = 6371000;

// Great-circle distance between a and b, in meters, via the haversine
// formula.
export function distanceMeters(a: Coordinates, b: Coordinates): number {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const deltaLat = ((b.lat - a.lat) * Math.PI) / 180;
  const deltaLng = ((b.lng - a.lng) * Math.PI) / 180;

  const h = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

  return EARTH_RADIUS_METERS * c;
}

const METERS_PER_MILE = 1609.344;
const METERS_PER_KM = 1000;

export type UnitSystem = 'imperial' | 'metric';

// Only the US, Liberia, and Myanmar use miles as the everyday public unit --
// everywhere else is metric. Issue #316: locale-driven, not a settings
// toggle (that's explicitly a follow-up product decision per the issue).
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

// Hermes/Expo ship a real Intl implementation (confirmed: no
// expo-localization dependency needed for this), so the device's own
// locale is enough to infer the region without a native module.
//
// CLDR likely-subtags fills in a region for a bare language tag (e.g.
// "en" -> "US", "fr" -> "FR"), so this is only wrong when the device's
// OWN locale setting is itself regionless -- uncommon (iOS/Android
// locales are region-tagged in practice, e.g. "en-GB"), but possible
// on an unconfigured emulator or an SSR/test environment. Falls back
// to imperial in that case, same as the outer catch below.
export function getUnitSystem(): UnitSystem {
  try {
    const locale = Intl.NumberFormat().resolvedOptions().locale;
    const region = new Intl.Locale(locale).maximize().region;
    return region && IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric';
  } catch {
    return 'imperial';
  }
}

// formatDistance turns a raw meter figure into the copy this app
// actually shows next to a thread -- e.g. "0.3 mi away", "1.2 mi
// away". This is a genuinely hyperlocal app (design.md's own framing),
// so almost every real distance lands well under 10 miles: one decimal
// place there keeps the number legible without false precision (a
// bare meter count reads as an odd thing for a stranger to know about
// you). Above 10 miles a whole-number mile is plenty -- nobody needs
// "14.7 mi away" vs "15.1 mi away" to matter for a hyperlocal thread.
//
// Below 0.1 mi (~160m) "0.0 mi away" would read as a bug (zero
// distance implies the same spot), so that band gets its own plain-
// words phrasing instead of a misleadingly precise decimal -- matching
// this app's voice (clear, direct, no invented precision) without
// escalating into a feet/yards unit switch this app doesn't otherwise use.
// Shared whole-mile rounding for every "radius in miles" display --
// the profile screen's own settings helper text and the Threads
// screen's "Within ~N mi of you" frame both need the same rule, so it
// lives here once rather than twice. Floors at "under one mile" rather
// than "≈0 miles" for the same reason formatDistance floors at "Less
// than 0.1 mi away" -- a rounded-to-zero radius reads as a bug, not a
// real value.
function radiusInUnit(radiusM: number, unit: UnitSystem): { rounded: number; isUnderOne: boolean } {
  const perUnit = unit === 'metric' ? METERS_PER_KM : METERS_PER_MILE;
  const rounded = Math.round(radiusM / perUnit);
  return { rounded, isUnderOne: rounded < 1 };
}

// Issue #151 AC1: the Threads screen's own radius-aware eyebrow --
// e.g. "Within ~3 mi of you" -- built from the user's real
// OwnProfile.radiusM rather than a static "Nearby" label.
export function radiusFrameLabel(radiusM: number, unit: UnitSystem = getUnitSystem()): string {
  const { rounded, isUnderOne } = radiusInUnit(radiusM, unit);
  const label = unit === 'metric' ? 'km' : 'mi';
  return isUnderOne ? `Within less than 1 ${label} of you` : `Within ~${rounded} ${label} of you`;
}

// The profile settings screen's own radius helper text -- e.g.
// "≈ 3 miles" / "≈ 5 km" next to the raw meter input.
export function radiusHelperText(radiusM: number, unit: UnitSystem = getUnitSystem()): string {
  const { rounded, isUnderOne } = radiusInUnit(radiusM, unit);
  if (unit === 'metric') return isUnderOne ? '< 1 km' : `≈ ${rounded} km`;
  return isUnderOne ? '< 1 mile' : `≈ ${rounded} mile${rounded === 1 ? '' : 's'}`;
}

// Sub-1-mile band collapsed to plain words, not a decimal -- founder,
// 2026-09-19, real safety concern: "less than 0.1 miles is a scarily
// close number to share when someone has bad intentions." The old
// version only softened the sub-0.1mi case; 0.1-0.9mi still printed a
// precise decimal ("0.3 mi away"), which at a hyperlocal scale is
// roughly street-block resolution -- combined across a person's own
// several thread posts, precise enough to meaningfully narrow down
// where a reader actually is. Distances at 1mi+ keep their existing
// decimal precision (the "why it matters" case is specifically the
// sub-mile band; a 0.1mi delta at 5mi away isn't a comparable
// triangulation risk).
export function formatDistance(meters: number, unit: UnitSystem = getUnitSystem()): string {
  const label = unit === 'metric' ? 'km' : 'mi';
  const distance = meters / (unit === 'metric' ? METERS_PER_KM : METERS_PER_MILE);

  if (distance < 1) return `Less than 1 ${label} away`;

  // Branch on the ROUNDED value, not the raw one: a raw `distance` of
  // e.g. 9.96 is < 10, but toFixed(1) rounds it up to "10.0" for
  // display -- branching on the raw value would print "10.0 mi away"
  // right next to the >= 10 branch's own "10 mi away", an inconsistent
  // decimal point at the one boundary the two branches share (final
  // review, PR #52). `oneDecimalPlace` stays a STRING (not `Number(...)`)
  // so an exact tenth like "5.0" keeps its trailing zero for display;
  // only the boundary comparison needs the numeric value.
  const oneDecimalPlace = distance.toFixed(1);

  if (Number(oneDecimalPlace) < 10) return `${oneDecimalPlace} ${label} away`;

  return `${Math.round(distance)} ${label} away`;
}
